import { useState, useEffect, useRef, useCallback } from 'react';
import { Brain, Play, Square, SkipForward, Trophy, Clock, Users, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { attendeeDisplayName } from '../../utils/attendeeDisplayName';
import { Event } from '../../services/events';
import {
  TriviaGame,
  TriviaAnswerKey,
  TriviaRuleSet,
  TriviaPlayerState,
  createTriviaGame,
  startGame,
  pushNextQuestion,
  endCurrentQuestion,
  revealGame,
  completeGame,
  cancelGame,
  subscribeToTriviaGame,
  subscribeToTriviaAnswerKey,
  getLeaderboard,
  getGameHistory,
} from '../../services/trivia';
import { TriviaClue, subscribeToLockedClues } from '../../services/triviaClues';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../services/firebase';
import type { PrizeType } from '../../services/prizes';
interface TriviaPanelProps {
  eventId: string;
  djId: string;
  event: Event;
  setEvent: React.Dispatch<React.SetStateAction<Event | null>>;
  onCreated?: () => void;
}

export default function TriviaPanel({ eventId, djId, event: _event, setEvent: _setEvent, onCreated }: TriviaPanelProps) {
  const [game, setGame] = useState<TriviaGame | null>(null);
  const [answerKey, setAnswerKey] = useState<TriviaAnswerKey | null>(null);
  const [lockedClues, setLockedClues] = useState<TriviaClue[]>([]);
  const [selectedClueIds, setSelectedClueIds] = useState<string[]>([]);
  const [ruleSet, setRuleSet] = useState<TriviaRuleSet>('most_correct');
  const [questionTime, setQuestionTime] = useState(15);
  const [reviewDuration, setReviewDuration] = useState(20);
  const [prizeType, setPrizeType] = useState<PrizeType>('shoutout');
  const [prizeCustomText, setPrizeCustomText] = useState('');
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [leaderboard, setLeaderboard] = useState<TriviaPlayerState[]>([]);
  const [history, setHistory] = useState<TriviaGame[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showClueList, setShowClueList] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const unsubGame = subscribeToTriviaGame(eventId, setGame);
    const unsubClues = subscribeToLockedClues(eventId, (clues) => {
      setLockedClues(clues);
      setSelectedClueIds(prev => prev.length === 0 ? clues.map(c => c.id) : prev);
    });
    return () => { unsubGame(); unsubClues(); };
  }, [eventId]);

  useEffect(() => {
    if (!game?.id) {
      setAnswerKey(null);
      return;
    }
    return subscribeToTriviaAnswerKey(game.id, setAnswerKey);
  }, [game?.id]);

  useEffect(() => {
    if (game?.status === 'active' && game.currentQuestionStartedAt) {
      const startedAt = new Date(game.currentQuestionStartedAt).getTime();
      const endAt = startedAt + game.questionTimeSeconds * 1000;

      const tick = () => {
        const remaining = Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
        setTimeLeft(remaining);
        if (remaining <= 0) {
          if (timerRef.current) clearInterval(timerRef.current);
          handleEndQuestion();
        }
      };

      tick();
      timerRef.current = setInterval(tick, 250);
      return () => { if (timerRef.current) clearInterval(timerRef.current); };
    } else if (game?.status === 'review') {
      const createdAt = new Date(game.updatedAt).getTime();
      const endAt = createdAt + game.reviewDurationSeconds * 1000;

      const tick = () => {
        const remaining = Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
        setTimeLeft(remaining);
      };

      tick();
      timerRef.current = setInterval(tick, 250);
      return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }
  }, [game?.status, game?.currentQuestionStartedAt, game?.updatedAt]);

  useEffect(() => {
    if (game?.status === 'revealing' && game.id) {
      getLeaderboard(game.id).then(setLeaderboard).catch(() => {});
    }
  }, [game?.status, game?.id]);

  const handleStartGame = useCallback(async () => {
    if (selectedClueIds.length < 4) {
      toast.error('Need at least 4 clues to run a game');
      return;
    }
    setLoading(true);
    try {
      const gameId = await createTriviaGame(eventId, djId, {
        ruleSet,
        reviewDurationSeconds: reviewDuration,
        questionTimeSeconds: questionTime,
        prizeType,
        prizeCustomText: prizeCustomText || undefined,
        clueIds: selectedClueIds,
      });
      await startGame(gameId);
      const selectedClues = lockedClues.filter(c => selectedClueIds.includes(c.id));
      for (const clue of selectedClues) {
        const clueDocId = `${eventId}_${clue.songRequestId}`;
        updateDoc(doc(db, 'triviaClues', clueDocId), {
          timesUsed: increment(1),
        }).catch(() => {});
      }
      toast.success('Trivia game started!');
      onCreated?.();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to start game';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [eventId, djId, ruleSet, reviewDuration, questionTime, prizeType, prizeCustomText, selectedClueIds, onCreated]);

  async function handlePushNext() {
    if (!game) return;
    try {
      await pushNextQuestion(game.id);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to push question';
      toast.error(msg);
    }
  }

  async function handleEndQuestion() {
    if (!game) return;
    try {
      await endCurrentQuestion(game.id);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to end question';
      toast.error(msg);
    }
  }

  async function handleReveal() {
    if (!game) return;
    try {
      await revealGame(game.id);
      toast.success('Game results revealed!');
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to reveal results';
      toast.error(msg);
    }
  }

  async function handleComplete() {
    if (!game) return;
    try {
      await completeGame(game.id);
      toast.success('Trivia game completed!');
    } catch (error) {
      toast.error('Failed to complete game');
    }
  }

  async function handleCancel() {
    if (!game) return;
    try {
      await cancelGame(game.id);
      toast.success('Trivia game cancelled');
    } catch (error) {
      toast.error('Failed to cancel game');
    }
  }

  function toggleClueSelection(clueId: string) {
    setSelectedClueIds(prev =>
      prev.includes(clueId) ? prev.filter(id => id !== clueId) : [...prev, clueId]
    );
  }

  function toggleSelectAll() {
    if (selectedClueIds.length === lockedClues.length) {
      setSelectedClueIds([]);
    } else {
      setSelectedClueIds(lockedClues.map(c => c.id));
    }
  }

  // --- Rendering ---

  if (!game || game.status === 'completed' || game.status === 'cancelled') {
    return (
      <div className="space-y-4">
        {/* Clue count */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-500" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">{lockedClues.length} clues ready</span>
          </div>
          <button
            onClick={() => setShowClueList(!showClueList)}
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
          >
            {showClueList ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {showClueList ? 'Hide' : 'Preview'}
          </button>
        </div>

        {showClueList && lockedClues.length > 0 && (
          <div className="max-h-40 overflow-y-auto space-y-1.5">
            {lockedClues.map(clue => (
              <div key={clue.id} className="flex items-start gap-2 text-xs p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <input
                  type="checkbox"
                  checked={selectedClueIds.includes(clue.id)}
                  onChange={() => toggleClueSelection(clue.id)}
                  className="mt-0.5 rounded border-gray-300 text-indigo-600"
                />
                <div className="min-w-0">
                  <p className="font-medium text-gray-700 dark:text-gray-300 truncate">{clue.songTitle} — {clue.songArtist}</p>
                  <p className="text-gray-500 dark:text-gray-400 italic truncate">{clue.clueText}</p>
                </div>
              </div>
            ))}
            <button onClick={toggleSelectAll} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">
              {selectedClueIds.length === lockedClues.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        )}

        {lockedClues.length < 4 && (
          <p className="text-xs text-amber-600 dark:text-amber-400">Need at least 4 clues to run a game. Curate clues from played songs first.</p>
        )}

        {/* Config */}
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Rule Set</label>
            <div className="space-y-1.5">
              {([
                { value: 'first_correct', label: 'First Correct Answer', desc: 'First to answer correctly wins each question' },
                { value: 'most_correct', label: 'Most Correct Answers', desc: 'Highest score across all questions wins' },
                { value: 'sudden_death', label: 'Sudden Death', desc: 'Wrong answer = eliminated. Last one standing wins' },
              ] as const).map(opt => (
                <label key={opt.value} className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                  <input
                    type="radio"
                    name="ruleSet"
                    value={opt.value}
                    checked={ruleSet === opt.value}
                    onChange={() => {
                      setRuleSet(opt.value);
                      setQuestionTime(opt.value === 'sudden_death' ? 5 : 15);
                    }}
                    className="mt-0.5 text-indigo-600"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{opt.label}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Time per question (sec)</label>
              <input
                type="number"
                min={5}
                max={30}
                value={questionTime}
                onChange={(e) => setQuestionTime(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>
            {ruleSet === 'sudden_death' && (
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Review duration (sec)</label>
                <input
                  type="number"
                  min={10}
                  max={60}
                  value={reviewDuration}
                  onChange={(e) => setReviewDuration(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Prize</label>
            <select
              value={prizeType}
              onChange={(e) => setPrizeType(e.target.value as PrizeType)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            >
              <option value="shoutout">Shoutout</option>
              <option value="pick_next_song">Pick Next Song</option>
              <option value="auto_queue_bypass">Auto Queue Bypass</option>
              <option value="custom">Custom</option>
            </select>
            {prizeType === 'custom' && (
              <input
                type="text"
                value={prizeCustomText}
                onChange={(e) => setPrizeCustomText(e.target.value)}
                placeholder="Describe the custom prize..."
                className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            )}
          </div>
        </div>

        <button
          onClick={handleStartGame}
          disabled={selectedClueIds.length < 4 || loading}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          <Play className="w-4 h-4" />
          {loading ? 'Starting...' : 'Start Trivia Game'}
        </button>

        {/* History */}
        <button
          onClick={async () => {
            if (!showHistory) {
              const h = await getGameHistory(eventId);
              setHistory(h);
            }
            setShowHistory(!showHistory);
          }}
          className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
        >
          {showHistory ? 'Hide history' : 'Past games'}
        </button>
        {showHistory && history.length > 0 && (
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {history.map(g => (
              <div key={g.id} className="text-xs p-2 bg-gray-50 dark:bg-gray-800 rounded flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">{g.ruleSet.replace('_', ' ')} — {g.questionCount} questions</span>
                <span className="text-gray-500 dark:text-gray-500">
                  {g.winnerNicknames.length > 0 ? `Winner: ${g.winnerNicknames[0]}` : 'No winner'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // --- Active Game States ---

  if (game.status === 'review') {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Review Phase</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Players are studying the song list</p>
        </div>

        <div className="text-center">
          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{timeLeft}s</div>
          <div className="flex items-center justify-center gap-1.5 mt-2 text-sm text-gray-600 dark:text-gray-400">
            <Users className="w-4 h-4" /> {game.playerCount} players joined
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handlePushNext}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm"
          >
            <Play className="w-4 h-4" /> Start Questions
          </button>
          <button
            onClick={handleCancel}
            className="px-3 py-2 border border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (game.status === 'active' || game.status === 'between_questions') {
    const isLastQuestion = game.currentQuestionIndex >= game.totalQuestions - 1;

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Question {game.currentQuestionIndex + 1} of {game.totalQuestions}
          </span>
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {game.activePlayers}</span>
            {game.status === 'active' && (
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {timeLeft}s</span>
            )}
          </div>
        </div>

        {/* Timer Bar */}
        {game.status === 'active' && (
          <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-purple-500 transition-all duration-250"
              style={{ width: `${(timeLeft / game.questionTimeSeconds) * 100}%` }}
            />
          </div>
        )}

        {/* Clue */}
        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center">
          <p className="text-gray-800 dark:text-gray-200 font-medium">{game.currentClueText}</p>
        </div>

        {/* Choices (DJ sees correct highlighted) */}
        {game.currentChoices && (
          <div className="grid grid-cols-2 gap-2">
            {game.currentChoices.map(songId => (
              <div
                key={songId}
                className={`p-2 rounded-lg border text-xs text-center ${
                  songId === answerKey?.currentCorrectSongId
                    ? 'border-green-400 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 font-medium'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                {songId.substring(0, 8)}...
                {songId === answerKey?.currentCorrectSongId && <CheckCircle className="w-3 h-3 inline ml-1" />}
              </div>
            ))}
          </div>
        )}

        {/* First Correct Winner Banner */}
        {game.ruleSet === 'first_correct' && game.firstCorrectWinnerId && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center gap-2">
            <Trophy className="w-5 h-5 text-green-600 dark:text-green-400" />
            <span className="text-sm font-medium text-green-700 dark:text-green-400">
              Winner: {game.firstCorrectWinnerNickname}!
            </span>
          </div>
        )}

        {/* Sudden Death Player Count */}
        {game.ruleSet === 'sudden_death' && (
          <p className="text-sm text-center text-gray-500 dark:text-gray-400">
            {game.activePlayers} player{game.activePlayers !== 1 ? 's' : ''} remaining
          </p>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          {game.status === 'between_questions' && !isLastQuestion && (
            <button
              onClick={handlePushNext}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm"
            >
              <SkipForward className="w-4 h-4" /> Next Question
            </button>
          )}
          <button
            onClick={handleReveal}
            className={`${game.status === 'between_questions' && !isLastQuestion ? '' : 'flex-1'} flex items-center justify-center gap-1.5 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium text-sm px-4`}
          >
            <Trophy className="w-4 h-4" /> {isLastQuestion || game.status === 'between_questions' ? 'End & Reveal' : 'End Early'}
          </button>
          <button
            onClick={handleCancel}
            className="px-3 py-2 border border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-sm"
          >
            <Square className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (game.status === 'revealing') {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <Trophy className="w-10 h-10 text-amber-500 mx-auto mb-2" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Game Over!</h3>
        </div>

        {game.winnerNicknames.length > 0 ? (
          <div className="text-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <p className="text-sm text-gray-500 dark:text-gray-400">Winner</p>
            <p className="text-xl font-bold text-amber-600 dark:text-amber-400">{game.winnerNicknames[0]}</p>
          </div>
        ) : (
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">No winner this round</p>
        )}

        {/* Leaderboard (most_correct) */}
        {game.ruleSet === 'most_correct' && leaderboard.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Leaderboard</p>
            {leaderboard.slice(0, 5).map((p, i) => (
              <div key={p.id} className={`flex items-center justify-between p-2 rounded-lg text-sm ${
                i === 0 ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-gray-50 dark:bg-gray-800'
              }`}>
                <span className="text-gray-700 dark:text-gray-300">
                  {i + 1}. {attendeeDisplayName(p.nickname, p.attendeeId)}
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  {p.correctCount} correct · {(p.cumulativeTimeMs / 1000).toFixed(1)}s
                </span>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={handleComplete}
          className="w-full py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
        >
          Complete Game
        </button>
      </div>
    );
  }

  return null;
}
